import { Spacing } from '@persona-id/design-tokens';
import { ReactNode, useCallback } from 'react';
import { WrappedFieldProps } from 'redux-form';
import { FormError } from 'shared/components/FormControl';
import { COLORS, SPACINGS } from 'shared/styles/variables';
import styled, { css } from 'styled-components';

import FormControlLabel from 'dashboard/components/FormControls/FormControlLabel';
import FormGroup from 'dashboard/layouts/FormLayout/FormGroup';
import Radio from 'design-system/components/Radio';
import RadioGroup from 'design-system/components/RadioGroup';

const StyledFormGroup = styled(FormGroup)<{ horizontal: boolean }>`
  display: flex;
  flex-direction: ${(props) => (props.horizontal ? 'row' : 'column')};
`;

const VerticalDiv = styled('div')<{ isCompact: boolean }>`
  display: grid;
  grid-auto-rows: auto;

  ${(props) =>
    !props.isCompact &&
    css`
      margin-bottom: ${SPACINGS.base * 3}px;
    `}
`;

const RadioGroupStyled = styled(RadioGroup)<{ isCompact?: boolean }>`
  ${(props) =>
    !props.isCompact &&
    css`
      & > div {
        margin-bottom: ${SPACINGS.base * 3}px;
      }
    `}
`;

const Label = styled('p')<{ isDisabled: boolean }>`
  display: inline-block;
  margin-bottom: 0;

  ${(props) => (props.isDisabled ? `color: ${COLORS.gray20};` : '')}
`;

const Description = styled(Label)<{ isDisabled: boolean }>`
  color: ${COLORS.gray50};
  ${(props) => (props.isDisabled ? `color: ${COLORS.gray20};` : '')}
  margin-top: ${Spacing[2]};
`;

const ChildContainer = styled('div')`
  margin-top: ${SPACINGS.base}px;
`;

export interface RadioGroupProps {
  className?: string;
  disabled?: boolean;
  isCompact?: boolean;
  horizontal?: boolean;
  radioLabelValues: RadioLabelValue[];
  label?: string;
  value: string;
}

interface RadioLabelValue {
  label: ReactNode;
  description?: ReactNode;
  value: string;
  disabled?: boolean;
  children?: ReactNode;
}

const RadioGroupControl = (props: WrappedFieldProps & RadioGroupProps) => {
  const {
    className,
    disabled,
    isCompact = false,
    horizontal = false,
    radioLabelValues,
    input,
    meta,
    label,
    ...passThroughProps
  } = props;

  const currentValue = input.value;

  // Pretend the full radio group is a single input component
  const handleChange = useCallback(
    (newVal: unknown) => {
      input.onChange(newVal);
    },
    [input.onChange],
  );

  return (
    <StyledFormGroup className={className} horizontal={horizontal}>
      {label != null && <FormControlLabel>{label}</FormControlLabel>}
      <RadioGroupStyled
        value={currentValue}
        onChange={handleChange}
        horizontal={horizontal}
        isCompact={isCompact}
      >
        {radioLabelValues.map(
          ({ label, description, value: itemValue, disabled: itemDisabled, children }, index) => {
            const id = `${input.name}[${index}]`;
            const isDisabled = disabled || itemDisabled;

            return (
              <Radio
                {...input}
                {...meta}
                {...passThroughProps}
                id={id}
                label={
                  props.horizontal ? (
                    <>
                      {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
                      <Label isDisabled={isDisabled}>{label}</Label>
                      {description && (
                        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                        <Description isDisabled={isDisabled}>{description}</Description>
                      )}
                      {children && <ChildContainer>{children}</ChildContainer>}
                    </>
                  ) : (
                    <VerticalDiv isCompact={isCompact}>
                      {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
                      <Label isDisabled={isDisabled}>{label}</Label>
                      {description && (
                        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                        <Description isDisabled={isDisabled}>{description}</Description>
                      )}
                      {children && <ChildContainer>{children}</ChildContainer>}
                    </VerticalDiv>
                  )
                }
                value={itemValue}
                disabled={isDisabled}
                key={index}
              />
            );
          },
        )}
      </RadioGroupStyled>
      {meta.error && meta.submitFailed && <FormError>{meta.error}</FormError>}
    </StyledFormGroup>
  );
};

export default RadioGroupControl;
